'use client';

import { useFocusRing } from '@react-aria/focus';
import { useLayoutEffect } from '@react-aria/utils';
import clsx from 'clsx';
import { useRef, useState, type ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps } from '../types.js';

export interface CodeBlockProps extends CommonProps {
  /** The code content to display. */
  code: string;
}

function CodeBlock(
  { code, UNSAFE_className, UNSAFE_style, ...props }: CodeBlockProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  // Most of the scrollable logic is copied from `FocusableContainer`, but the scrollable element is a child of the container, not the container itself.
  const [isScrollable, setIsScrollable] = useState(false);
  const scrollableRef = useRef<HTMLPreElement>(null);
  const { focusProps, isFocusVisible } = useFocusRing();

  useLayoutEffect(() => {
    const scrollableElement = scrollableRef.current;
    const isSSR = typeof window === 'undefined';

    let scrollableResizeObserver: ResizeObserver | undefined;

    if (scrollableElement && !isSSR) {
      scrollableResizeObserver = new window.ResizeObserver((entries) => {
        if (entries[0]) {
          const { target } = entries[0];
          const isScrollableHorizontal = target.scrollWidth > target.clientWidth;
          const isScrollableVertical = target.scrollHeight > target.clientHeight;

          setIsScrollable(isScrollableHorizontal || isScrollableVertical);
        }
      });

      scrollableResizeObserver.observe(scrollableElement);
    }

    return () => {
      if (scrollableElement && scrollableResizeObserver) {
        scrollableResizeObserver.unobserve(scrollableElement);
      }
    };
  }, [scrollableRef]);

  return (
    <div
      {...props}
      ref={ref}
      className={clsx('cim-code-block', UNSAFE_className)}
      style={UNSAFE_style}
      data-focus-visible={isFocusVisible || undefined}
    >
      <pre {...focusProps} ref={scrollableRef} tabIndex={isScrollable ? 0 : -1}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * Displays preformatted code content in a styled container.
 *
 * See [code block usage guidelines](https://ui.cimpress.io/components/code-block).
 */
const _CodeBlock = withStyleProps(forwardRef(CodeBlock), 'CodeBlock');
export { _CodeBlock as CodeBlock };
